/**
 * Governance Reviews Component
 * Displays governance reviews for a program
 */
import React, { useState, useEffect } from 'react'
import { Shield, CheckCircle, XCircle, AlertCircle, Plus, Filter, X } from 'lucide-react'
import { programGovernanceService, GovernanceReview } from '../../services/programGovernance'
import { toast } from 'sonner'
import { getErrorMessage } from '../../utils/apiTransformers'
import type { ApiErrorLike } from '../../utils/apiError'

interface GovernanceReviewsProps {
  programId: number
}

const EMPTY_REVIEW = {
  review_type: 'stage_gate',
  outcome: 'approved',
  notes: '',
  reviewer_role: '',
  review_date: new Date().toISOString().split('T')[0],
}

export default function GovernanceReviews({ programId }: GovernanceReviewsProps) {
  const [reviews, setReviews] = useState<GovernanceReview[]>([])
  const [loading, setLoading] = useState(true)
  const [filterType, setFilterType] = useState<string>('all')
  const [filterOutcome, setFilterOutcome] = useState<string>('all')
  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState<typeof EMPTY_REVIEW>(EMPTY_REVIEW)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadReviews()
  }, [programId, filterType, filterOutcome])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await programGovernanceService.createReview(programId, formData)
      toast.success('Governance review created')
      setShowModal(false)
      setFormData(EMPTY_REVIEW)
      loadReviews()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to create review')
    } finally {
      setSaving(false)
    }
  }

  const loadReviews = async () => {
    setLoading(true)
    try {
      const data = await programGovernanceService.listReviews(
        programId,
        filterType !== 'all' ? filterType : undefined,
        filterOutcome !== 'all' ? filterOutcome : undefined
      )
      setReviews(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error loading governance reviews:', error)
      toast.error(getErrorMessage(error) || 'Failed to load governance reviews')
    } finally {
      setLoading(false)
    }
  }

  const getOutcomeIcon = (outcome: string) => {
    switch (outcome) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />
      case 'action_required':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />
      default:
        return <AlertCircle className="w-5 h-5 text-[#829ab1]" />
    }
  }

  const getOutcomeColor = (outcome: string) => {
    switch (outcome) {
      case 'approved':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200'
      case 'action_required':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-[#f0f4f8] rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-[#f0f4f8] rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-[#486581]" />
          <h3 className="text-xl font-semibold text-[#102a43]">Governance Reviews</h3>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          New Review
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#829ab1]" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-1.5 border border-[#bcccdc] rounded-lg text-sm text-[#102a43] bg-white"
          >
            <option value="all">All Types</option>
            <option value="stage_gate">Stage Gate</option>
            <option value="risk_assessment">Risk Assessment</option>
            <option value="financial">Financial</option>
            <option value="compliance">Compliance</option>
          </select>
        </div>
        <select
          value={filterOutcome}
          onChange={(e) => setFilterOutcome(e.target.value)}
          className="px-3 py-1.5 border border-[#bcccdc] rounded-lg text-sm text-[#102a43] bg-white"
        >
          <option value="all">All Outcomes</option>
          <option value="approved">Approved</option>
          <option value="action_required">Action Required</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <Shield className="w-12 h-12 text-[#829ab1] mx-auto mb-4" />
          <p className="text-[#829ab1]">No governance reviews found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="p-4 border border-[#bcccdc] rounded-lg hover:border-[#486581] hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getOutcomeIcon(review.outcome)}
                    <h4 className="font-medium text-[#102a43] capitalize">
                      {review.review_type.replace('_', ' ')}
                    </h4>
                    <span className={`px-2 py-0.5 text-xs rounded border ${getOutcomeColor(review.outcome)}`}>
                      {review.outcome.replace('_', ' ')}
                    </span>
                    {review.reviewer_role && (
                      <span className="text-xs text-[#829ab1]">
                        ({review.reviewer_role})
                      </span>
                    )}
                  </div>
                  {review.notes && (
                    <p className="text-sm text-[#829ab1] mb-2">{review.notes}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-[#829ab1]">
                    <span>Date: {new Date(review.review_date).toLocaleDateString()}</span>
                    <span>Created: {new Date(review.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Create Governance Review Modal */}
    {showModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#102a43]">New Governance Review</h2>
            <button onClick={() => { setShowModal(false); setFormData(EMPTY_REVIEW) }} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <form onSubmit={handleCreate} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Review Type</label>
                <select value={formData.review_type} onChange={e => setFormData(p => ({ ...p, review_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900">
                  <option value="stage_gate">Stage Gate</option>
                  <option value="risk_assessment">Risk Assessment</option>
                  <option value="financial">Financial</option>
                  <option value="compliance">Compliance</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Outcome</label>
                <select value={formData.outcome} onChange={e => setFormData(p => ({ ...p, outcome: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900">
                  <option value="approved">Approved</option>
                  <option value="action_required">Action Required</option>
                  <option value="rejected">Rejected</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reviewer Role</label>
                <input type="text" value={formData.reviewer_role} onChange={e => setFormData(p => ({ ...p, reviewer_role: e.target.value }))}
                  placeholder="e.g. Board, PMO"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Review Date</label>
                <input type="date" value={formData.review_date} onChange={e => setFormData(p => ({ ...p, review_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] text-gray-900" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] resize-none text-gray-900" />
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
              <button type="button" onClick={() => { setShowModal(false); setFormData(EMPTY_REVIEW) }}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] disabled:opacity-50 flex items-center gap-2">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus className="w-4 h-4" />}
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  )
}

